import { useEffect, useRef, useState } from 'react';
import { addChatActivityListener, removeChatActivityListener, getLastChatActivity } from '../../services/chatSessionService';

/**
 * A component that manages idle detection for the chat
 * 
 * @param {Object} props
 * @param {boolean} props.enabled - Whether idle detection is enabled
 * @param {number} props.idleTimeout - Timeout in seconds before showing idle message
 * @param {number} props.closeTimeout - Timeout in seconds before closing chat after idle message
 * @param {Function} props.onIdle - Callback when user becomes idle
 * @param {Function} props.onClose - Callback when chat should close due to inactivity
 * @param {boolean} props.hasMessages - Whether there are messages in the chat
 * @param {boolean} props.isChatClosed - Whether the chat is closed
 */
const IdleDetectionManager = ({
  enabled,
  idleTimeout,
  closeTimeout,
  onIdle,
  onClose,
  hasMessages,
  isChatClosed
}) => {
  // State to track if idle message has been shown
  const [idleMessageShown, setIdleMessageShown] = useState(false);
  const idleMessageShownRef = useRef(false);

  // Keep the ref in sync with state
  useEffect(() => {
    idleMessageShownRef.current = idleMessageShown;
  }, [idleMessageShown]);
  const closedByIdleRef = useRef(false); // Track if closed by idle

  // References to keep track of timers
  const idleTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Track previous isChatClosed (for future use or debugging)
  const prevIsChatClosedRef = useRef(isChatClosed);
  useEffect(() => {
    prevIsChatClosedRef.current = isChatClosed;
  }, [isChatClosed]);

  // Convert seconds to milliseconds
  const idleTimeoutMs = idleTimeout * 1000;
  const closeTimeoutMs = closeTimeout * 1000;

  // Clear all timers
  const clearAllTimers = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Function to handle chat activity
  const handleChatActivity = () => {
    console.log('🔥 IdleDetectionManager: Chat activity detected - DEBUGGING');

    // Clear existing timers
    clearAllTimers();

    // Reset idle message state
    if (idleMessageShown) {
      console.log('🔥 IdleDetectionManager: Resetting idle state');
      setIdleMessageShown(false);
    }

    // Only reset closedByIdleRef on real user activity
    if (closedByIdleRef.current) {
      closedByIdleRef.current = false;
      console.log('🔥 IdleDetectionManager: Reset closedByIdleRef due to user activity');
    }

    // Only set new idle timer if enabled and chat is open
    if (enabled && !isChatClosed) {
      console.log('🔥 IdleDetectionManager: Setting idle timer for', idleTimeoutMs, 'ms');
      idleTimerRef.current = setTimeout(() => {
        handleIdleTimeout();
      }, idleTimeoutMs);
    } else {
      console.log('🔥 IdleDetectionManager: NOT setting idle timer - enabled:', enabled, 'isChatClosed:', isChatClosed);
    }
  };

  // Function to handle when idle timeout is reached
  const handleIdleTimeout = () => {
    console.log('IdleDetectionManager: Idle timeout reached');
    // Only show idle message if there are messages and we haven't shown it yet and not just closed
    // Prevent duplicate idle message if close is already pending
    if (
      hasMessages &&
      !idleMessageShownRef.current &&
      !isChatClosed &&
      !closedByIdleRef.current &&
      closeTimerRef.current === null
    ) {
      console.log('IdleDetectionManager: Showing idle message');

      // Update state and ref synchronously
      setIdleMessageShown(true);
      idleMessageShownRef.current = true;

      // Call the onIdle callback
      if (onIdle) {
        onIdle();
      }

      // Set close timer
      console.log('IdleDetectionManager: Setting close timer for', closeTimeoutMs, 'ms');
      closeTimerRef.current = setTimeout(() => {
        handleCloseTimeout();
      }, closeTimeoutMs);
    }
  };

  // Function to handle when close timeout is reached
  const handleCloseTimeout = () => {
    console.log('IdleDetectionManager: Close timeout reached');

    // Only close if we've shown the idle message and the chat is still open
    if (idleMessageShown && !isChatClosed) {
      console.log('IdleDetectionManager: Closing chat');
      closedByIdleRef.current = true; // Set flag to suppress idle message

      // Call the onClose callback
      if (onClose) {
        onClose();
      }

      // Reset state
      setIdleMessageShown(false);
    }
  };

  // Set up idle detection on mount and when dependencies change
  useEffect(() => {
    console.log('🔥 IdleDetectionManager: Setting up idle detection - DEBUGGING', {
      enabled,
      idleTimeout,
      closeTimeout,
      hasMessages,
      isChatClosed,
      idleMessageShown
    });
    // Clear any existing timers
    clearAllTimers();
    // Add chat activity listener
    addChatActivityListener(handleChatActivity);
    console.log('🔥 IdleDetectionManager: Added activity listener');

    // Initial check for inactivity
    if (enabled && !isChatClosed) {
      const now = Date.now();
      const lastActivity = getLastChatActivity();
      const timeSinceLastActivity = now - lastActivity;
      console.log('🔥 IdleDetectionManager: Initial check - DEBUGGING', {
        timeSinceLastActivity,
        idleTimeoutMs,
        lastActivity: new Date(lastActivity).toISOString(),
        now: new Date(now).toISOString()
      });
      if (idleMessageShown) {
        // If idle message is already shown, just set the close timer
        console.log('IdleDetectionManager: Idle message already shown, setting close timer');
        closeTimerRef.current = setTimeout(() => {
          handleCloseTimeout();
        }, closeTimeoutMs);
      } else if (
        timeSinceLastActivity >= idleTimeoutMs &&
        !window._justReopenedChat &&
        !closedByIdleRef.current // Prevent duplicate idle message after close
      ) {
        // If already idle, show idle message immediately, but NOT if just reopened or just closed
        if (closedByIdleRef.current) {
          console.log('IdleDetectionManager: Already closed by idle, not showing idle message again');
        } else {
          console.log('IdleDetectionManager: Already idle, showing message immediately');
          handleIdleTimeout();
        }
      } else {
        // Otherwise, set timer for remaining time until idle
        const remainingTime = Math.max(0, idleTimeoutMs - timeSinceLastActivity);
        console.log('IdleDetectionManager: Setting timer for remaining time:', remainingTime);
        idleTimerRef.current = setTimeout(() => {
          handleIdleTimeout();
        }, remainingTime);
      }
      // Reset the just reopened flag after setup
      if (window._justReopenedChat) {
        window._justReopenedChat = false;
      }
    }
    // Cleanup function
    return () => {
      console.log('IdleDetectionManager: Cleaning up');
      removeChatActivityListener(handleChatActivity);
      clearAllTimers();
    };
  }, [enabled, idleTimeout, closeTimeout, hasMessages, isChatClosed, idleTimeoutMs, closeTimeoutMs, idleMessageShown]);

  // This component doesn't render anything
  return null;
};

export default IdleDetectionManager;